/**
 * The Ask layer's browser adapter: the whole retrieval runs in the page, with no backend. The
 * compiled catalog (`src/ask/core/catalog.data.ts`) ships in the bundle, the question a user types
 * never leaves the browser, and only the two telemetry events go out — through the sink set with
 * `setTelemetrySink` (the console until one is plugged in).
 *
 * Roles come from the client, which a user controls. That is fine: roles only reorder results and
 * never hide an entry.
 */
import { handleFeedback, handleSearch, type ValidationErrorBody } from "../ask/core/handlers";
import type {
  AssistantFeedback,
  AssistantFeedbackResponse,
  AssistantSearchRequest,
  AssistantSearchResponse,
  Principal,
  TenantId,
} from "../ask/core/types";

export { setTelemetrySink, type TelemetrySink } from "../ask/core/interaction-log";

/**
 * One tier, and no user id: the settle window that merges a keystroke burst into one miss-log row
 * is per principal, and a browser holds one user at a time — so no identifier needs to reach the
 * telemetry at all.
 */
let principal: Principal | null = { roles: ["employee"] };
let tenantId: TenantId = null;

export const setAskPrincipal = (next: Principal | null, tenant: TenantId = null): void => {
  principal = next;
  tenantId = tenant;
};

/** Thrown when the body the panel built does not validate — a programming error, not a user one. */
export class AskClientError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown
  ) {
    super(
      `ask-client: ${status} ${
        (body as Partial<ValidationErrorBody>)?.error?.issues
          ?.map((issue) => `${issue.path || "(root)"}: ${issue.message}`)
          .join("; ") ?? "request rejected"
      }`
    );
    this.name = "AskClientError";
  }
}

export const searchAssistant = async (
  args: AssistantSearchRequest
): Promise<AssistantSearchResponse> => {
  const result = await handleSearch(args, principal, tenantId);
  if (result.status !== 200) throw new AskClientError(result.status, result.body);
  return result.body as AssistantSearchResponse;
};

export const sendAssistantFeedback = async (
  args: AssistantFeedback
): Promise<AssistantFeedbackResponse> => {
  const result = await handleFeedback(args, principal, tenantId);
  if (result.status !== 202) throw new AskClientError(result.status, result.body);
  return result.body as AssistantFeedbackResponse;
};
